import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Signal, TemplateRef, viewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  CheckoutTouristIn,
  CheckoutTransferIn,
  CheckoutTransferOut,
  CheckoutTransferPrice,
  CheckoutTransferTypeEnum
} from '@appTypes/api.types';
import { WebsiteFlags } from '@core/services/website-flags.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CheckoutTouristTransferTypedForm } from '@pages/checkout-page/checkout.typed-forms';
import { CheckoutState } from '@pages/checkout-page/state/checkout.state';
import { SelectorComponent } from '@shared/components/selector/selector.component';
import { KzInputDirective } from '@shared/directives/kz-input.directive';
import { TypedTemplateDirective } from '@shared/directives/typed-template.directive';
import { CartState } from '@shared/modules/cart/state/cart.state';
import { DynamicModalRef, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { environment } from '@env/environment';

export interface CheckoutTransferAndVisaParams {
  tourist: CheckoutTouristIn;
  index: number;
  canCopy: boolean;
}
export interface CheckoutTransferAndVisaSaveData {
  selectedVisaId: string;
  transfers: CheckoutTransferIn[];
  copy: boolean;
}
@Component({
  selector: 'app-checkout-transfer-and-visa-form',
  templateUrl: './checkout-transfer-and-visa-form.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    KzInputDirective,
    MatCheckbox,
    MatButton,
    TypedTemplateDirective,
    AsyncPipe,
    DatePipe,
    PriceFormatPipe,
    TranslatePipe,
    SelectorComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutTransferAndVisaFormComponent implements OnDynamicModal {
  readonly #store = inject(Store);
  /** website-flags R-WF108: the source chip of every car (list, selected, included) follows `website-details-tags`. */
  protected readonly detailsTagsHidden = inject(WebsiteFlags).detailsTagsHidden;
  readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');

  readonly dateFormat = environment.formats.date;
  readonly dateTimeFormat = environment.formats.dateTime;

  modalRef!: DynamicModalRef;

  formGroup = new FormGroup({
    selectedVisaId: new FormControl<string | null>(null),
    transfers: new FormArray<FormGroup<CheckoutTouristTransferTypedForm>>([]),
    copy: new FormControl<boolean>(true)
  });
  visas$ = this.#store.select(CheckoutState.visas);
  readonly servicesSelectorTemplateTypedToken!: { option: CheckoutTransferPrice };
  readonly headLineTemplateTypedToken!: {
    params: {
      typeName: string;
      flightNumber?: string;
      terminalCode?: string;
      dateTime?: Date;
      date?: Date;
      time?: string;
    };
  };

  priceCurrency$ = this.#store.select(CheckoutState.priceCurrency);

  transfersData!: Signal<CheckoutTransferOut[]>;
  data!: CheckoutTransferAndVisaParams;

  /** The car the form currently holds for a block (the cheapest one for an included transfer), or null. */
  selectedCar(transferData: CheckoutTransferOut, index: number): CheckoutTransferPrice | null {
    const selected =
      this.formGroup.controls.transfers.controls[index]?.value.selectedTransferPriceId ??
      transferData.selectedTransferPriceId;
    return transferData.transferPrices?.find((p) => p.id === selected) ?? null;
  }

  /**
   * R-CK33 (docs features/website/checkout.md, website-web#139): true when the window lists at least one car with a price the agent can choose,
   * so the caption "Prices include your agency surcharge" is shown under the cars. An included Arrival / Departure car shows no price - no caption for it alone.
   */
  showsSurchargeNote(): boolean {
    return (this.transfersData?.() ?? []).some(
      (leg) =>
        !(leg.requirement === 'included' && leg.type !== CheckoutTransferTypeEnum.HotelToHotel) &&
        (leg.transferPrices ?? []).some((car) => !!car.adultPrice || !!car.childPrice)
    );
  }

  getHotelByPriceId$ = (priceId: string) => this.#store.select(CartState.getHotelByPriceId(priceId));

  getFlight$ = (flightId: string) => this.#store.select(CartState.getFlight(flightId));

  onDynamicModal(data: CheckoutTransferAndVisaParams): void {
    this.data = data;
    if (data.tourist.id)
      this.transfersData = this.#store.selectSignal(CheckoutState.getTouristTransfersData(data.tourist.id));
    this.#init();
  }
  #init() {
    if (!this.data.tourist?.id) return;
    if (this.data.tourist.transfers?.length) {
      this.data.tourist.transfers.forEach(() =>
        this.formGroup.controls.transfers.push(new FormGroup(new CheckoutTouristTransferTypedForm()))
      );
      this.formGroup.controls.transfers.patchValue(this.data.tourist.transfers);
      this.formGroup.controls.selectedVisaId.setValue(this.data.tourist.selectedVisaId ?? null);
    }
  }

  onSubmit() {
    if (this.formGroup.valid) this.modalRef.close(this.formGroup.getRawValue());
    else this.formGroup.markAllAsTouched();
  }
}
